// ---------------------------------------------------------------------------
// A panel above the page that is NOT a form dialog - a ticket preview, a
// "the delivery has left" notice, an offline-sale confirmation.
//
// It gives those panels the same manners as `Modal` without the header,
// footer and focus policy a form needs: a dimmed backdrop that closes the
// panel when clicked, Escape that closes the topmost panel only, and a
// click inside that closes nothing. Panels that use it inherit that
// behaviour rather than each writing its own listener.
// ---------------------------------------------------------------------------

import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { onBackdropClick, useOverlayDismiss } from './overlayStack';

interface OverlayProps {
  open: boolean;
  /// Asked to close: by the backdrop, by Escape. The caller decides what
  /// that means - usually setting its own `open` false.
  onDismiss: () => void;
  /// Read out as the panel's name.
  label: string;
  children: ReactNode;
  /// Where the panel sits: centred, or at the top of a scrolling backdrop
  /// (a tall receipt preview).
  align?: 'center' | 'start';
  /// Stacking, as a Tailwind class - previews sit above dialogs.
  zIndexClass?: string;
}

export function Overlay({ open, onDismiss, label, children, align = 'center', zIndexClass = 'z-[95]' }: OverlayProps) {
  useOverlayDismiss(open, onDismiss);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      data-overlay-backdrop
      onClick={onBackdropClick(onDismiss)}
      className={clsx(
        'fixed inset-0 flex justify-center bg-charcoal/50 p-4',
        align === 'start' ? 'items-start overflow-y-auto' : 'items-center',
        zIndexClass,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
